/**
 * Quick-poll body — question + one-tap options, with a soft "maybe later".
 *
 * Presentational only (props in, callbacks out), so the live student experience
 * and the admin preview render identical UI. The student wrapper lives in
 * student-app/src/whatsNewExperiences/QuickPollExperience.tsx.
 */

import { useState } from 'react';
import './QuickPollView.css';

export interface PollOption { label: string; value: string }

export const DEFAULT_POLL_OPTIONS: PollOption[] = [
    { label: '👍 Yes', value: 'yes' },
    { label: '👎 No', value: 'no' },
];

export interface QuickPollViewProps {
    question: string;
    options: PollOption[];
    /** Fired on tap, before the acknowledgement. */
    onPick: (value: string) => void;
    onLater: () => void;
}

export function QuickPollView({ question, options, onPick, onLater }: QuickPollViewProps) {
    const [submitted, setSubmitted] = useState<string | null>(null);

    const pick = (value: string) => {
        if (submitted) return;
        setSubmitted(value);
        onPick(value);
    };

    return (
        <div className="wn-quickpoll">
            {submitted ? (
                <p className="wn-quickpoll-thanks">Thanks for the feedback! 🙌</p>
            ) : (
                <>
                    <h3 className="wn-quickpoll-q">{question}</h3>
                    <div className="wn-quickpoll-options">
                        {options.map(o => (
                            <button key={o.value} type="button" className="wn-quickpoll-btn" onClick={() => pick(o.value)}>
                                {o.label}
                            </button>
                        ))}
                    </div>
                    <button type="button" className="wn-quickpoll-later" onClick={onLater}>Maybe later</button>
                </>
            )}
        </div>
    );
}

/** Read a poll's question/options out of a tip's opaque `experienceData`. */
export function toPollConfig(data: Record<string, unknown> | undefined): { question: string; options: PollOption[] } {
    return {
        question: typeof data?.question === 'string' ? data.question : 'How are we doing?',
        options: Array.isArray(data?.options) && (data.options as PollOption[]).length > 0
            ? (data.options as PollOption[])
            : DEFAULT_POLL_OPTIONS,
    };
}
